import { addMilliseconds, isEqual, min, subMilliseconds } from 'date-fns';
import type {
  CalendarConstraintContext,
  CalendarConstraint,
  CalendarInterval,
  CalendarItem,
  CalendarCursor,
  CalendarResolution,
  CalendarConstraintHint,
} from './types';

export function randomId(): string {
  return String((Math.random() * 0x7fffffff) | 0);
}

/**
 * 날짜 구간의 모든 구간이 조건에 해당하는지 (즉, 연속적인 구간이 모두 조건을 충족하는지) 확인합니다.
 */
export function isEntireSelectionAvailable(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  selection: CalendarInterval,
): boolean {
  let cursor = selection.from;
  while (cursor < selection.to) {
    const hint = constraint(context, cursor);
    if (!hint.available) return false;
    if (hint.until == null) return true;
    const next = hint.until;
    if (isEqual(cursor, next)) {
      throw new Error('Infinite loop detected');
    }
    cursor = next;
  }
  return true;
}

/**
 * 특정 시점이 조건에 해당하는지 확인합니다.
 */
export function isInstantAvailable(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  instant: Date,
): boolean {
  if (context.current?.from != null && context.current.from > instant) {
    return false;
  }
  return constraint(context, instant).available;
}

/**
 * 캘린더 항목이 유효한지 확인합니다.
 */
export function isItemValid(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  item: CalendarItem,
): boolean {
  // instant를 지정하는 경우 to가 유효한 지만 검사하도록 특수 처리
  if (isEqual(item.from, item.to)) {
    return isInstantAvailable(constraint, { ...context, current: { ...item, to: undefined }, focus: 'to' }, item.to);
  }
  const fromValid = isInstantAvailable(
    constraint,
    { ...context, current: { ...item, from: undefined, to: undefined }, focus: 'from' },
    item.from,
  );
  const toValid = isInstantAvailable(
    constraint,
    { ...context, current: { ...item, to: undefined }, focus: 'to' },
    subMilliseconds(item.to, 1),
  );
  return fromValid && toValid;
}

/**
 * 현재 수정중인 커서의 세부 항목이 유효한지 확인합니다.
 */
export function isCursorFocusValid(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  cursor?: CalendarCursor | null,
  target?: 'from' | 'to',
): boolean {
  const currentTarget = target ?? cursor?.focus ?? 'from';
  const item = cursor?.item;
  if (currentTarget === 'from') {
    return (
      item?.from != null &&
      isInstantAvailable(
        constraint,
        { ...context, current: { ...item, from: undefined, to: undefined }, focus: 'from' },
        item.from,
      )
    );
  } else {
    return (
      item?.to != null &&
      isInstantAvailable(
        constraint,
        { ...context, current: { ...item, to: undefined }, focus: 'to' },
        subMilliseconds(item.to, 1),
      )
    );
  }
}

/**
 * 현재 수정중인 커서의 입력된 값이 유효한지 확인합니다. 입력되지 않은 값은 무시합니다.
 */
export function isCursorPartiallyValid(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  cursor?: CalendarCursor | null,
): boolean {
  const item = cursor?.item;
  return (
    (item?.from == null || isCursorFocusValid(constraint, context, cursor, 'from')) &&
    (item?.to == null || isCursorFocusValid(constraint, context, cursor, 'to'))
  );
}

/**
 * selection 구간 안에서 유효한 날짜 구간을 모두 반환합니다.
 */
export function getAvailableIntervals(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  selection: CalendarInterval,
): CalendarInterval[] {
  const output: CalendarInterval[] = [];
  let cursor = selection.from;
  while (cursor < selection.to) {
    const hint = constraint(context, cursor);
    if (hint.available) {
      output.push({
        from: cursor,
        to: min([hint.until ?? selection.to, selection.to]),
      });
    }
    if (hint.until == null) {
      break;
    }
    const next = hint.until;
    if (isEqual(cursor, next)) {
      throw new Error('Infinite loop detected');
    }
    cursor = next;
  }
  return output;
}

/**
 * selection 구간 안에서 첫 번째로 유효한 시점을 힌트와 함께 반환합니다.
 */
export function getFirstAvailableInSelectionWithHint(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  selection: CalendarInterval,
): { date: Date | null; hint: CalendarConstraintHint | null } {
  let cursor = selection.from;
  let lastHint: CalendarConstraintHint | null = null;
  while (cursor < selection.to) {
    const hint = constraint(context, cursor);
    if (hint.available) return { date: cursor, hint };
    if (hint.until == null) return { date: null, hint };
    const next = hint.until;
    if (isEqual(cursor, next)) {
      throw new Error('Infinite loop detected');
    }
    cursor = next;
    lastHint = hint;
  }
  return { date: null, hint: lastHint };
}

/**
 * selection 구간 안에서 첫 번째로 유효한 시점을 반환합니다.
 */
export function getFirstAvailableInSelection(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  selection: CalendarInterval,
): Date | null {
  return getFirstAvailableInSelectionWithHint(constraint, context, selection).date;
}

/**
 * selection 구간 안에 유효한 시점이 하나라도 있는지 확인합니다.
 */
export function isSelectionAvailable(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  selection: CalendarInterval,
): boolean {
  return getFirstAvailableInSelection(constraint, context, selection) != null;
}

/**
 * selection 구간 안에서 마지막으로 유효한 시점을 반환합니다. (non-inclusive기 때문에 실제 유효한 instant를 얻기 위해서는 1밀리초를 빼야 합니다)
 */
export function getLastAvailableInSelection(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  selection: CalendarInterval,
): Date | null {
  const intervals = getAvailableIntervals(constraint, context, selection);
  const last = intervals.at(-1);
  if (last == null) {
    return null;
  }
  return last.to;
}

/**
 * 해당 instant 시점이 item 구간 안에 있는지 확인합니다.
 */
export function isInstantWithinItem(item: CalendarInterval, instant: Date): boolean {
  return item.from <= instant && instant < item.to;
}

/**
 * 두 instant 사이에서 겹치는 구간이 있는지 확인합니다.
 */
export function isIntervalOverlappingWithItem(item: CalendarInterval, interval: CalendarInterval): boolean {
  return item.from < interval.to && item.to > interval.from;
}

/**
 * 아이템 목록 중에서 해당 instant 시점에 겹치는 항목을 반환합니다.
 */
export function findItemAtInstant(items: CalendarItem[], instant: Date): CalendarItem | null {
  for (const item of items) {
    if (isInstantWithinItem(item, instant)) {
      return item;
    }
  }
  return null;
}

/**
 * 아이템 목록 중에서 해당 instant 시점에 겹치는 항목들을 반환합니다.
 */
export function findItemsOverlappingWithInterval(items: CalendarItem[], interval: CalendarInterval): CalendarItem[] {
  return items.filter((item) => isIntervalOverlappingWithItem(item, interval));
}

/**
 * interval 구간 중에서 해당 item 항목의 from을 설정할 수 있는지 여부를 힌트와 함께 반환합니다.
 */
export function isSelectionFromAvailableWithHint(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  item: Partial<CalendarItem>,
  interval: CalendarInterval,
  /**
   * 해당 구간을 설정했을 때 to가 유효하지 않게 되는 경우에도 유효한 것으로 보도록 합니다.
   */
  canOverwriteInvariant: boolean = false,
): { available: boolean; hint: CalendarConstraintHint | null } {
  const from = getFirstAvailableInSelectionWithHint(
    constraint,
    { ...context, current: { ...item, from: undefined, to: undefined }, focus: 'from' },
    interval,
  );
  // from을 설정했을 때 to의 조건이 불성립하게 변경되는지 확인합니다.
  if (from.date != null && item.to != null && !canOverwriteInvariant) {
    const toValid = constraint(
      { ...context, current: { ...item, from: from.date, to: undefined }, focus: 'to' },
      subMilliseconds(item.to, 1),
    );
    if (!toValid.available) {
      return { available: false, hint: toValid };
    }
  }

  return { available: from.date != null, hint: from.hint };
}

/**
 * interval 구간 중에서 해당 item 항목의 from을 설정할 수 있는지 여부를 반환합니다.
 */
export function isSelectionFromAvailable(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  item: Partial<CalendarItem>,
  interval: CalendarInterval,
  /**
   * 해당 구간을 설정했을 때 to가 유효하지 않게 되는 경우에도 유효한 것으로 보도록 합니다.
   */
  canOverwriteInvariant: boolean = false,
): boolean {
  return isSelectionFromAvailableWithHint(constraint, context, item, interval, canOverwriteInvariant).available;
}

/**
 * interval 구간 중에서 해당 item 항목의 from을 최대한 이르게 설정합니다.
 * @param overwriteInvariant to가 유효하지 않게 되는 상황에서 to를 초기화시킬 지 여부입니다.
 */
export function applyFromWithinInterval(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  item: Partial<CalendarItem>,
  interval: CalendarInterval,
  overwriteInvariant: boolean = true,
): Partial<CalendarItem> {
  const from = getFirstAvailableInSelection(
    constraint,
    { ...context, current: { ...item, from: undefined, to: undefined }, focus: 'from' },
    interval,
  );
  if (from == null) {
    throw new Error('Unable to find available instant');
  }
  if (item.to != null && from >= item.to) {
    if (overwriteInvariant) {
      return { ...item, from, to: undefined };
    } else {
      throw new Error('to > from invariant failed');
    }
  }
  // from의 변경으로 인해 to가 더 이상 유효하지 않게 되는 경우 to를 초기화합니다.
  if (item.to != null && overwriteInvariant) {
    const toValid = isInstantAvailable(
      constraint,
      { ...context, current: { ...item, from, to: undefined }, focus: 'to' },
      subMilliseconds(item.to, 1),
    );
    if (!toValid) {
      return { ...item, from, to: undefined };
    }
  }
  return { ...item, from };
}

/**
 * interval 구간 중에서 해당 item 항목의 to을 설정할 수 있는지 여부를 힌트와 함께 반환합니다.
 */
export function isSelectionToAvailableWithHint(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  item: Partial<CalendarItem>,
  interval: CalendarInterval,
  /**
   * 해당 구간을 설정했을 때 from이 유효하지 않게 되는 경우에도 유효한 것으로 보도록 합니다.
   */
  canOverwriteInvariant: boolean = false,
): { available: boolean; hint: CalendarConstraintHint | null } {
  if (canOverwriteInvariant) {
    const to = getFirstAvailableInSelectionWithHint(
      constraint,
      { ...context, current: { ...item, from: undefined, to: undefined }, focus: 'to' },
      interval,
    );
    return { available: to.date != null, hint: to.hint };
  }

  const to = getFirstAvailableInSelectionWithHint(
    constraint,
    { ...context, current: { ...item, to: undefined }, focus: 'to' },
    interval,
  );

  return { available: to.date != null, hint: to.hint };
}

/**
 * interval 구간 중에서 해당 item 항목의 to를 설정할 수 있는지 여부를 반환합니다.
 */
export function isSelectionToAvailable(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  item: Partial<CalendarItem>,
  interval: CalendarInterval,
  /**
   * 해당 interval을 설정했을 때 from이 유효하지 않게 되는 경우에도 유효한 것으로 보도록 합니다.
   */
  canOverwriteInvariant: boolean = false,
): boolean {
  return isSelectionToAvailableWithHint(constraint, context, item, interval, canOverwriteInvariant).available;
}

/**
 * interval 구간 중에서 해당 item 항목의 to를 최대한 늦게 설정합니다.
 * @param overwriteInvariant from이 유효하지 않게 되는 상황에서 from을 초기화시킬 지 여부입니다.
 */
export function applyToWithinInterval(
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  item: Partial<CalendarItem>,
  interval: CalendarInterval,
  overwriteInvariant: boolean = true,
): Partial<CalendarItem> {
  const to = getLastAvailableInSelection(
    constraint,
    { ...context, current: { ...item, to: undefined }, focus: 'to' },
    interval,
  );
  if (to == null) {
    // from이 초기화되었을 때 설정이 가능한 경우 from을 초기화합니다.
    if (item.from != null && overwriteInvariant) {
      const toWithFromReset = getLastAvailableInSelection(
        constraint,
        { ...context, current: { ...item, from: undefined, to: undefined }, focus: 'to' },
        interval,
      );
      if (toWithFromReset != null) {
        return { ...item, to: toWithFromReset, from: undefined };
      }
    }
    throw new Error('Unable to find available instant');
  }
  if (item.from != null && to <= item.from) {
    if (overwriteInvariant) {
      return { ...item, to, from: undefined };
    } else {
      throw new Error('to > from invariant failed');
    }
  }
  return { ...item, to };
}

export function isCursorItemInterval(item: Partial<CalendarItem> | null | undefined): item is CalendarInterval {
  return item?.from != null && item.to != null;
}

export function isCursorItemComplete(item: Partial<CalendarItem> | null | undefined): item is CalendarItem {
  return item?.from != null && item.to != null && item.id != null;
}

export function getIntervalOrNull(item: Partial<CalendarInterval> | null | undefined): CalendarInterval | null {
  return isCursorItemInterval(item) ? item : null;
}

/**
 * subinterval 구간이 interval 구간의 from에 속하는지 확인합니다.
 */
export function isSubintervalStartOfInterval(
  subinterval: CalendarInterval,
  interval: Pick<CalendarInterval, 'from'>,
): boolean {
  return isInstantWithinItem(subinterval, interval.from);
}

/**
 * subinterval 구간이 interval 구간의 to에 속하는지 확인합니다.
 */
export function isSubintervalEndOfInterval(
  subinterval: CalendarInterval,
  interval: Pick<CalendarInterval, 'to'>,
): boolean {
  return isInstantWithinItem(subinterval, subMilliseconds(interval.to, 1));
}

/**
 * subinterval 구간이 interval 구간의 from/to에 속하지 않지만, 그 사이에 끼어있는지 확인합니다.
 */
export function isSubintervalMiddleOfInterval(subinterval: CalendarInterval, interval: CalendarInterval): boolean {
  return (
    isIntervalOverlappingWithItem(subinterval, interval) &&
    !isSubintervalStartOfInterval(subinterval, interval) &&
    !isSubintervalEndOfInterval(subinterval, interval)
  );
}

/**
 * 해당 item 항목의 from/to를 interval과 target에 맞게 설정합니다. 실패하는 경우 오류를 반환합니다.
 * @param overwriteInvariant to < from이 되는 상황에서 from/to를 초기화시킬 지 여부입니다.
 */
export function applyItemInterval(
  item: Partial<CalendarItem>,
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  interval: CalendarInterval,
  target: 'from' | 'to' | 'all',
  overwriteInvariant: boolean = true,
): { item: Partial<CalendarItem>; dateSet: Date | null } {
  let nextItem = item;
  let dateSet: Date | null = null;
  if (['from', 'all'].includes(target)) {
    nextItem = applyFromWithinInterval(constraint, context, nextItem, interval, overwriteInvariant);
    dateSet = nextItem.from ?? null;
  }
  if (['to', 'all'].includes(target)) {
    nextItem = applyToWithinInterval(constraint, context, nextItem, interval, overwriteInvariant);
    if (target !== 'all') {
      dateSet = nextItem.to ?? null;
    }
  }
  return { item: nextItem, dateSet };
}

/**
 * 해당 item 항목의 from/to를 interval과 target에 맞게 설정합니다. 실패하는 경우 fallbackInterval을 설정해봅니다.
 * @param overwriteInvariant to < from이 되는 상황에서 from/to를 초기화시킬 지 여부입니다.
 */
export function applyItemIntervalWithFallback(
  item: Partial<CalendarItem>,
  constraint: CalendarConstraint,
  context: CalendarConstraintContext,
  interval: CalendarInterval,
  fallbackInterval: CalendarInterval,
  target: 'from' | 'to' | 'all',
  overwriteInvariant: boolean = true,
): { item: Partial<CalendarItem>; dateSet: Date | null } {
  try {
    return applyItemInterval(item, constraint, context, interval, target, overwriteInvariant);
  } catch (e) {
    return applyItemInterval(item, constraint, context, fallbackInterval, target, overwriteInvariant);
  }
}

/**
 * donorFrom/donorTo 값을 활용해, date로부터 오프셋 (시각)이 동일한 interval 구간을 계산합니다.
 * 예: donorFrom이 2025-01-01 14:00:00이고, resolution이 day일 때, date를 2025-03-01 12:34:56으로 넣으면,
 * from: 2025-03-01 14:00:00, to: 2025-03-02 00:00:00 인 interval이 반환됩니다.
 */
export function resolveIntervalWithOffsets(
  resolution: CalendarResolution,
  date: Date,
  donorFrom?: Date | null,
  donorTo?: Date | null,
): CalendarInterval {
  const interval = resolution.resolve(date);

  const donorFromOffset = donorFrom != null ? donorFrom.valueOf() - resolution.start(donorFrom).valueOf() : 0;
  const donorToOffset = donorTo != null ? resolution.end(subMilliseconds(donorTo, 1)).valueOf() - donorTo.valueOf() : 0;

  return {
    from: addMilliseconds(interval.from, donorFromOffset),
    to: subMilliseconds(interval.to, donorToOffset),
  };
}

export function getNextFocus(cursor: CalendarCursor | null, target?: 'all' | 'from' | 'to' | null): 'from' | 'to' {
  switch (target) {
    case 'all':
      return 'from';
    case 'from':
      return 'from';
    case 'to':
      return 'to';
    case null:
    case undefined:
      return cursor?.focus === 'to' ? 'from' : 'to';
  }
}

export function getNewCursorItem(): Partial<CalendarItem> {
  return { id: randomId() };
}

export function ensureItemId(item: Partial<CalendarItem> | null): Partial<CalendarItem> | null {
  if (item == null) {
    return null;
  }
  if (item.id == null) {
    return { ...item, id: randomId() };
  }
  return item;
}
